import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { loginUser, verifyToken, verifyUser } from '../../api/authApi'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

const LoginForm = () => {
  document.title = 'Login'
  const [otpBox, setOtpBox] = useState(false)
  const navigate = useNavigate()

  const { login, isAuthenticated } = useAuth()

  useEffect(() => {
    document.title = 'Login'

    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/dealdashboard')
    }
  }, [isAuthenticated, navigate])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  const mutation = useMutation({
    mutationFn: loginUser,
    onSuccess: () => {
      toast.success('OTP Sent')
      setOtpBox(true)
    },
    onError: (error) => {
      console.error('Login error:', error)
      toast.error(
        error?.response?.data?.message ||
          'Login failed. Please check your credentials.'
      )
    },
  })

  const verifyMutation = useMutation({
    mutationFn: verifyUser,
    onSuccess: async () => {
      toast.success('Login successful')
      try {
        // After login, verify the token to get permissions
        const verifyResult = await verifyToken()

        if (verifyResult.status) {
          // Update auth context with user data including permissions
          login({
            fullName: verifyResult.data.fullName,
            role: verifyResult.data.role,
            permissions: verifyResult.data.permissions || [],
          })
          navigate('/dealdashboard')
        } else {
          toast.error('Session verification failed')
          navigate('/login')
          // Handle failed verification
        }
      } catch (error) {
        console.error('Verification error:', error)
        toast.error('Error verifying your session')
      }
    },
    onError: (error) => {
      console.error('Login error:', error)
      toast.error(
        error?.response?.data?.message ||
          'Login failed. Please check your credentials.'
      )
    },
  })

  const onSubmit = (data) => {
    otpBox ? verifyMutation.mutate(data) : mutation.mutate(data)
  }

  return (
    <div
      className="min-h-screen bg-cover bg-center flex items-center justify-center px-4"
      style={{ backgroundImage: "url('/images/bg-1.jpg')" }}
    >
      <div className="w-full max-w-md bg-white bg-opacity-90 backdrop-blur-sm rounded-md shadow-lg p-6">
        <div className="text-center mb-6">
          <img
            src="/images/logo.png"
            alt="Logo"
            className="h-8 mx-auto mb-2 mix-blend-multiply"
          />
          <h2 className="text-2xl font-bold text-gray-700">Login</h2>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <input
              type="text"
              placeholder="Email"
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
              {...register('username', {
                required: 'Email is required',
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: 'Invalid email address',
                },
              })}
            />
            {errors.username && (
              <p className="text-sm float-right text-red-500 my-1">
                {errors.username.message}
              </p>
            )}
          </div>

          {otpBox && (
            <div>
              <input
                type="text"
                placeholder="OTP"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                {...register('otp', {
                  required: 'OTP is required',
                })}
              />
              {errors.otp && (
                <p className="text-sm float-right text-red-500 my-1">
                  {errors.otp.message}
                </p>
              )}
            </div>
          )}

          {otpBox ? (
            <button
              type="submit"
              disabled={verifyMutation.isPending}
              className={`w-full font-medium py-2 px-4 rounded-md transition duration-200
    ${
      verifyMutation.isPending
        ? 'bg-orange-300 cursor-not-allowed'
        : 'bg-orange-500 hover:bg-orange-600'
    }
    text-white
  `}
            >
              {verifyMutation.isPending ? 'Logging in...' : 'Log In'}
            </button>
          ) : (
            <button
              type="submit"
              disabled={mutation.isPending}
              className={`w-full font-medium py-2 px-4 rounded-md transition duration-200
    ${
      mutation.isPending
        ? 'bg-orange-300 cursor-not-allowed'
        : 'bg-orange-500 hover:bg-orange-600'
    }
    text-white
  `}
            >
              {mutation.isPending ? 'OTP Getting...' : 'Get OTP'}
            </button>
          )}
        </form>
      </div>
    </div>
  )
}

export default LoginForm
